import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import WaitlistDialog from "@/components/WaitlistDialog";
import LeadCTA from "@/components/LeadCTA";
import ScoreBar from "@/components/ScoreBar";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, ChartBar, Crosshair, Key, Lock,
} from "@phosphor-icons/react";

const SAMPLE = {
  asin: "B0CN12X3YZ",
  title: "Stainless Steel Insulated Water Bottle — 32oz Vacuum Bottle …",
  overallScore: 72,
  scores: { seo: 64, images: 58, pricing: 82, content: 84 },
  recommendations: [
    { tag: "SEO", priority: "HIGH", title: "Move primary keyword into the first 80 characters",
      description: "Your title buries the primary keyword at position 5. Mobile SERPs truncate to ~80 chars." },
    { tag: "IMG", priority: "HIGH", title: "Add lifestyle infographic with scale",
      description: "7-slot stack uses 4. Adding scale + use-case shots typically lifts CTR ~9%." },
  ],
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asin, setAsin] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [waitlistCount, setWaitlistCount] = useState(null);

  useEffect(() => {
    api.get("/blogs?limit=3").then((r) => setBlogs(r.data)).catch(() => {});
    api.get("/waitlist/count").then((r) => setWaitlistCount(r.data.display)).catch(() => {});
  }, []);

  const startCheck = (e) => {
    e?.preventDefault?.();
    if (!asin.trim()) { toast.error("Paste an Amazon URL or ASIN to start"); return; }
    if (!user) { sessionStorage.setItem("pending_asin", asin.trim()); navigate("/signup"); }
    else navigate(`/tools/catalog-analyzer?q=${encodeURIComponent(asin.trim())}`);
  };

  return (
    <div className="bg-paper">
      {/* Marquee bar — persimmon brand strip */}
      <div className="border-b border-ink bg-persimmon text-white overflow-hidden">
        <div className="py-2.5 marquee-track text-[10px] font-mono uppercase tracking-[0.22em]">
          {["track-a", "track-b"].map((trackId) => (
            <React.Fragment key={trackId}>
              <span>CliqCommerce · A free analyst for Amazon sellers</span>
              <span>★</span>
              <span>Build v1.0 · Beta</span>
              <span>★</span>
              <span>{waitlistCount ? `${waitlistCount.toLocaleString()} sellers waiting` : "Waitlist open"}</span>
              <span>★</span>
              <span>Free tools · Login required · 5/day</span>
              <span>★</span>
              <span>No card · No upsell · No trial</span>
              <span>★</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-rule">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" aria-hidden />
        <div className="absolute inset-0 paper-scrim pointer-events-none" aria-hidden />
        {/* Floating persimmon block — editorial accent */}
        <div className="hidden md:block absolute right-[-80px] top-32 w-72 h-72 bg-persimmon -rotate-6 z-0" aria-hidden />
        <div className="hidden md:block absolute right-12 top-72 w-32 h-32 bg-butter z-0" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7 fade-up">
              <div className="flex items-center gap-3">
                <div className="label-mono">№ 01 · Free analyst, on demand</div>
                <span className="ribbon">New · Beta</span>
              </div>
              <h1 className="mt-6 font-display font-black tracking-tightest leading-[0.95] text-ink text-[56px] sm:text-7xl md:text-[88px]">
                Honest scores.<br />
                <span className="bg-persimmon text-white px-3 py-0 inline-block -rotate-1 leading-none mt-2">Specific fixes.</span><br />
                Your Amazon listings.
              </h1>
              <p className="mt-8 text-lg text-ink/70 max-w-xl leading-relaxed">
                A serious analyst replacing the $400/month tools. Paste any Amazon URL — get a real score,
                the exact fixes, ranked keywords, competitor breakdown. No fluff. No upsell.
              </p>

              <form onSubmit={startCheck} className="mt-10 flex flex-col sm:flex-row max-w-2xl border border-ink" data-testid="hero-form">
                <Input
                  data-testid="hero-asin-input"
                  value={asin}
                  onChange={(e) => setAsin(e.target.value)}
                  placeholder="Paste Amazon URL or 10-character ASIN…"
                  className="h-14 border-0 text-base flex-1 focus-visible:ring-0 focus-visible:border-0 bg-white"
                />
                <Button type="submit" data-testid="hero-analyze-btn"
                  className="h-14 px-6 text-base border-0 rounded-none sm:border-l sm:border-ink">
                  Analyze free <ArrowRight className="w-4 h-4 ml-2" weight="bold" />
                </Button>
              </form>
              <div className="mt-4 flex items-center gap-2 label-mono text-ink/60">
                <Lock className="w-3.5 h-3.5" weight="bold" />
                <span>Free login · 5 checks/tool/day · No card</span>
              </div>

              <div className="mt-10 flex flex-wrap gap-3 items-center">
                <WaitlistDialog />
                <LeadCTA sourcePage="landing" />
              </div>
            </div>

            <div className="md:col-span-5 fade-up-2">
              <SamplePreview />
            </div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY STRIP — what we replace */}
      <section className="border-b border-rule bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
          {[
            { k: "$400/mo", v: "Tools we replace", color: "text-persimmon" },
            { k: "5×", v: "Free runs per tool / day", color: "text-butter" },
            { k: "3", v: "AI analysts in your workspace", color: "text-signal" },
            { k: "0", v: "Credit card required", color: "text-white" },
          ].map((s) => (
            <div key={s.k} className="bg-ink px-5 py-3">
              <div className={`font-display font-black text-4xl tracking-tightest tabular ${s.color}`}>{s.k}</div>
              <div className="label-mono mt-2 text-white/60">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TOOLS / VALUE PROPS */}
      <section className="py-24 md:py-32 border-b border-rule block-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-6">
              <div className="label-mono">№ 02 · The workspace</div>
              <h2 className="mt-4 font-display font-bold text-4xl md:text-5xl tracking-tightest text-ink leading-[1.05]">
                Three analysts. One workspace.<br />Five runs of each. Daily.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 text-ink/70 leading-relaxed">
              No trial expiring in your face. No upsell pop-ups.
              We're growing a waitlist for a paid tier — you get the tools.
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 border-t border-rule">
            {[
              { icon: ChartBar, no: "01", title: "Catalog Analyzer", desc: "Paste any Amazon URL or ASIN. Get SEO, image, pricing & content scores plus ranked, prioritized fixes.", chip: "bg-persimmon text-white", iconColor: "text-persimmon" },
              { icon: Crosshair, no: "02", title: "Listing Optimizer", desc: "Rewrite your title and 5 bullets. Keyword-rich, conversion-tuned, with the reason for every change.", chip: "bg-cobalt text-white", iconColor: "text-cobalt" },
              { icon: Key, no: "03", title: "Keyword Generator", desc: "Enter a seed product or term. Get 15–20 ranked keywords with relevance and competition signals.", chip: "bg-butter text-ink", iconColor: "text-ink" },
            ].map((v, i) => (
              <div key={v.title}
                className={`p-8 border-b border-rule ${i < 2 ? "md:border-r" : ""} bg-white fade-up-${i+1} hover:bg-paper transition-colors`}
                data-testid={`feature-${i}`}>
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 ${v.chip} grid place-items-center`}>
                    <v.icon weight="bold" className="w-6 h-6" />
                  </div>
                  <span className="label-mono">{v.no}</span>
                </div>
                <div className="mt-10 font-display text-2xl font-bold tracking-tightest text-ink">{v.title}</div>
                <p className="mt-3 text-ink/65 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 border-b border-rule bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-mono">№ 03 · How it works</div>
          <h2 className="mt-4 font-display font-bold text-4xl md:text-5xl tracking-tightest text-ink leading-[1.05]">
            Three steps. About <span className="bg-butter px-2 -rotate-1 inline-block">two minutes.</span>
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-px bg-rule">
            {[
              { n: "01", t: "Sign up free", d: "Email or Google. Auto-verified. No card required.", color: "text-persimmon" },
              { n: "02", t: "Paste URL or ASIN", d: "We pull the listing and route it to the analyst (LLM).", color: "text-cobalt" },
              { n: "03", t: "Read the verdict", d: "Scores, recommendations, keywords, competitor table — export CSV/PDF.", color: "text-signal" },
            ].map((s) => (
              <div key={s.n} className="bg-white p-10 hover:bg-paper transition-colors">
                <div className={`font-display font-black tabular text-5xl ${s.color}`}>{s.n}</div>
                <div className="mt-6 font-display text-xl font-bold tracking-tight text-ink">{s.t}</div>
                <p className="mt-2 text-ink/65 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG TEASER */}
      <section className="py-24 md:py-32 border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <div>
              <div className="label-mono">№ 04 · Journal</div>
              <h2 className="mt-4 font-display font-bold text-4xl md:text-5xl tracking-tightest text-ink leading-[1.05]">
                Tactics that move the needle.
              </h2>
            </div>
            <Link to="/blog" className="label-mono inline-flex items-center hover:text-ink" data-testid="blog-see-all">
              All entries <ArrowUpRight className="w-3.5 h-3.5 ml-1" weight="bold" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-rule">
            {blogs.slice(0, 3).map((b, i) => (
              <Link key={b.slug} to={`/blog/${b.slug}`}
                className="bg-white p-8 group hover:bg-surface-hover transition-colors"
                data-testid={`blog-card-${b.slug}`}>
                <div className="flex items-center justify-between label-mono">
                  <span>№ {String(i + 1).padStart(2, "0")}</span>
                  <span>{(b.tags?.[0] || "Insight").toUpperCase()}</span>
                </div>
                <div className="mt-8 font-display text-2xl font-bold leading-tight tracking-tight text-ink group-hover:text-ink-2">
                  {b.title}
                </div>
                <p className="mt-3 text-ink/65 text-sm line-clamp-3 leading-relaxed">{b.excerpt}</p>
                <div className="mt-6 label-mono inline-flex items-center">
                  Read <ArrowUpRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" weight="bold" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST CTA — persimmon brand block */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="dots-persimmon text-white p-10 md:p-16 relative overflow-hidden border-2 border-ink">
            <div className="absolute -right-12 -top-12 w-44 h-44 bg-butter rotate-12 hidden md:block" aria-hidden />
            <div className="grid md:grid-cols-12 gap-10 items-end relative">
              <div className="md:col-span-7">
                <div className="label-mono text-white/80">№ 05 · The waitlist</div>
                <h3 className="mt-4 font-display font-black text-4xl md:text-6xl tracking-tightest leading-[0.98]">
                  Lock in launch pricing.<br />Higher limits.
                </h3>
                {waitlistCount && (
                  <p className="mt-6 label-mono text-white/80">{waitlistCount.toLocaleString()} sellers already waiting</p>
                )}
              </div>
              <div className="md:col-span-5 md:text-right">
                <WaitlistDialog
                  trigger={
                    <Button className="bg-ink text-white hover:bg-white hover:text-ink border-ink h-12 px-7 text-base" data-testid="cta-waitlist">
                      Join the waitlist <ArrowRight className="w-4 h-4 ml-2" weight="bold" />
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SamplePreview() {
  return (
    <div className="bg-white border border-ink fade-up-2" data-testid="hero-sample-preview">
      <div className="flex items-center justify-between border-b border-rule px-5 py-3 label-mono">
        <span>Sample · Catalog Analyzer</span>
        <span className="ticker text-ink/50">{SAMPLE.asin}</span>
      </div>
      <div className="p-6 space-y-6">
        <div className="text-sm text-ink/70 line-clamp-2 leading-relaxed">{SAMPLE.title}</div>

        <div>
          <div className="flex items-baseline justify-between mb-3">
            <span className="label-mono">Overall</span>
            <span className="font-mono tabular font-medium text-gold">
              {SAMPLE.overallScore}<span className="text-ink/30">/100</span>
            </span>
          </div>
          <ScoreBar value={SAMPLE.overallScore} showValue={false} />
        </div>

        <div className="grid grid-cols-2 gap-5">
          {Object.entries(SAMPLE.scores).map(([k, v]) => (
            <ScoreBar key={k} label={k} value={v} />
          ))}
        </div>

        <div className="border-t border-rule pt-5 space-y-3">
          {SAMPLE.recommendations.map((r) => (
            <div key={r.title} className="flex gap-3 items-start">
              <span className={`label-mono shrink-0 px-1.5 py-0.5 border ${r.priority === "HIGH" ? "border-alert text-alert" : "border-rule"}`}>
                {r.priority}
              </span>
              <div className="flex-1">
                <div className="text-sm font-medium text-ink leading-snug">{r.title}</div>
                <div className="text-xs text-ink/55 mt-1 leading-relaxed line-clamp-2">{r.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
